/**
 * KNOWLEDGE_DATASETS — 企业知识库里「整份由管理员显式发布」的数据集白名单（N-184）。
 *
 * 一个数据集 = 企业知识库（L5 `inc-knowledge/`）下一个固定目录里的几份 JSON 文件。员工电脑
 * 只从服务端同步拉取这一份；本机对 L5 镜像的写入不会上传（桌面 `isAutoUploadGatedLayer`），
 * 唯一的写入口是会话工具 `publish_knowledge_dataset`（只注册给企业管理员会话）→ 桌面主进程
 * 用企业会话发 `POST/PUT /api/enterprise/files`。
 *
 * 单一真相源：核心工具（读哪些文件、每份的 `kind`）与桌面主进程（写到哪个逻辑路径、
 * 发布前核对哪些底版）都只读这一份，不各写一张表。第一项是灵犀类目树
 * （`plugins/dosia-brand-marketing` 的 lingxi-category 库产出这三份文件 + `publish-base.json`）。
 *
 * 底版文件（`publish-base.json`）只在本机：合并脚本把「合并那一刻全公司那份每个文件的
 * sha256」记进去，桌面主进程发布前对服务端当前清单核一次——别人在你合并之后改过，就拒绝，
 * 不发任何写请求（服务端 `PUT` 的 `baseVersion` 是第二层）。
 */
import { KNOWLEDGE_L5_ROOT } from "./knowledge-submission.js";
export const KNOWLEDGE_DATASETS = {
    "lingxi-category": {
        targetDir: `${KNOWLEDGE_L5_ROOT}/foundations/lingxi-category`,
        files: [
            { fileName: "tree.json", kind: "lingxi-category-tree", required: true },
            { fileName: "attributes.json", kind: "lingxi-category-attributes", required: false },
            { fileName: "sightings.json", kind: "lingxi-category-sightings", required: true },
        ],
        baseFileName: "publish-base.json",
    },
};
/** 运行时 guard：模型 / 调用方给的 `dataset` 是不是白名单里的键（`Object.hasOwn`，不认原型链上的名字）。 */
export function isKnowledgeDatasetId(value) {
    return typeof value === "string" && Object.hasOwn(KNOWLEDGE_DATASETS, value);
}
export const KNOWLEDGE_DATASET_IDS = Object.keys(KNOWLEDGE_DATASETS).filter(isKnowledgeDatasetId);
/** 白名单里的数据集说明；不在白名单 → null。 */
export function getKnowledgeDataset(value) {
    return isKnowledgeDatasetId(value) ? KNOWLEDGE_DATASETS[value] : null;
}
/** 某个文件在企业知识库里的逻辑路径（服务端 `logicalPath`，layer = L5）。 */
export function knowledgeDatasetLogicalPath(spec, fileName) {
    return `${spec.targetDir}/${fileName}`;
}
// ── 文件内容的最低结构（发布前必过）─────────────────────────────
/**
 * 每种 kind 的最低结构核对：不是完整校验（完整守卫在产出它的插件库里），只挡「明显不是这份数据」
 * 的文件——空树、少了关键字段的半成品——别把它发给全公司。按 kind 登记（不往
 * KNOWLEDGE_DATASETS 的文件条目里加字段，插件侧的对账测试按条目原形解析那张表）。
 */
const BODY_SHAPE_CHECKS = {
    "lingxi-category-tree": (body) => {
        const reasons = [];
        if (body.schemaVersion !== 1)
            reasons.push(`schemaVersion 应为 1，实际是「${String(body.schemaVersion)}」`);
        const nodes = body.nodes;
        if (!Array.isArray(nodes))
            reasons.push("缺 nodes 数组");
        else if (nodes.length === 0)
            reasons.push("nodes 是空的（一个类目都没有）");
        return Array.isArray(nodes) ? { reasons, nodeCount: nodes.length } : { reasons };
    },
    "lingxi-category-attributes": (body) => ({
        reasons: isPlainRecord(body.byCategory) ? [] : ["缺 byCategory 对象"],
    }),
    "lingxi-category-sightings": (body) => {
        const reasons = [];
        if (!Array.isArray(body.reads))
            reasons.push("缺 reads 数组");
        if (!isPlainRecord(body.lastSeenAt))
            reasons.push("缺 lastSeenAt 对象");
        return { reasons };
    },
};
/**
 * 一份待发布文件解析后的值 → 合法（附树的节点数，预览展示用）或逐条原因。先核 `kind` 等于
 * `expectedKind`，再核该 kind 的最低结构。核心工具在预览与确认两次调用里都跑它，所以任何一条
 * 不过，什么请求都不会发出去。
 */
export function checkKnowledgeDatasetFileBody(expectedKind, value) {
    if (!isPlainRecord(value))
        return { ok: false, reasons: ["不是 JSON 对象"] };
    if (value.kind !== expectedKind) {
        return { ok: false, reasons: [`kind 应为「${expectedKind}」，实际是「${String(value.kind)}」`] };
    }
    const check = Object.hasOwn(BODY_SHAPE_CHECKS, expectedKind) ? BODY_SHAPE_CHECKS[expectedKind] : undefined;
    const shape = check ? check(value) : { reasons: [] };
    if (shape.reasons.length > 0)
        return { ok: false, reasons: shape.reasons };
    return shape.nodeCount !== undefined ? { ok: true, nodeCount: shape.nodeCount } : { ok: true };
}
const SHA256_HEX = /^[0-9a-f]{64}$/;
const PUBLISH_BASE_KEYS = new Set(["dataset", "base"]);
function isPlainRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}
/**
 * 反序列化 guard（红线 §9）：`publish-base.json` 解析后的值 → 合法底版，或逐条原因。
 * 严格：顶层只许 `dataset` / `base`；`dataset` 必须是 `expected`；`base` 的键必须是该数据集的
 * 可发布文件名、值必须是小写 sha256 hex 或 null。形状不对就拒绝发布——底版是并发保护的依据，
 * 猜一个就等于没有这道核对。
 */
export function parseKnowledgeDatasetPublishBase(raw, expected) {
    if (!isPlainRecord(raw))
        return { ok: false, reasons: ["不是 JSON 对象"] };
    const reasons = [];
    for (const key of Object.keys(raw)) {
        if (!PUBLISH_BASE_KEYS.has(key))
            reasons.push(`多出字段「${key}」`);
    }
    if (raw.dataset !== expected)
        reasons.push(`dataset 应为「${expected}」，实际是「${String(raw.dataset)}」`);
    const spec = KNOWLEDGE_DATASETS[expected];
    const allowed = new Set(spec.files.map((f) => f.fileName));
    const base = {};
    if (!isPlainRecord(raw.base)) {
        reasons.push("base 不是对象");
    }
    else {
        for (const [fileName, sha] of Object.entries(raw.base)) {
            if (!allowed.has(fileName)) {
                reasons.push(`base 里有不在发布清单内的文件「${fileName}」`);
                continue;
            }
            if (sha === null || (typeof sha === "string" && SHA256_HEX.test(sha))) {
                base[fileName] = sha;
            }
            else {
                reasons.push(`base.${fileName} 不是 sha256 或 null`);
            }
        }
    }
    if (reasons.length > 0)
        return { ok: false, reasons };
    return { ok: true, value: { dataset: expected, base } };
}
/**
 * 「全公司那份在你合并之后被改过」的唯一判据——核心工具预览与桌面主进程发布前共用。
 *
 * 对底版里记了的每个文件：服务端当前 sha256（不存在 = null）既不等于底版、也不等于这次要发的
 * 内容 → 算被别人改过。「等于这次要发的内容」放行，是为了让部分失败后重新发布能只补没成功
 * 的那几份：已成功的文件服务端已是新内容，不该再被当成冲突。
 */
export function findKnowledgeBaseMismatches(input) {
    const mismatched = [];
    for (const [fileName, baseSha] of Object.entries(input.base)) {
        const serverSha = input.server[fileName] ?? null;
        if (serverSha === baseSha)
            continue;
        if (serverSha !== null && input.local[fileName] === serverSha)
            continue;
        mismatched.push(fileName);
    }
    return mismatched;
}
//# sourceMappingURL=knowledge-datasets.js.map